import type * as React from "react";

import { IconButton } from "components/ui/IconButton";
import { CloseIcon } from "components/ui/Icons/CloseIcon";
import { useLocalOverlay } from "context/localOverlayContext";
import { useOnEscKeyDownEvent } from "hooks/useOnEscKeyDownEvent";
import { useStopPropagation } from "hooks/useStopPropagation";
import { useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

export interface IModalBaseProps {
  noTopCloseButton?: boolean; // Will not show the close button in the top right corner default: false
  parentElement?: HTMLElement; // default: document.body
}

export interface IModalProps extends IModalBaseProps {
  actions?: React.ReactNode;
  content?: React.ReactNode;
  isOpen: boolean;
  keepOpenOnClickOutside?: boolean;
  onClose?: () => void;
  unwrapContent?: boolean;
}

type TPortalContentProps = Omit<IModalProps, "isOpen" | "parentElement"> & TWithTestId;

const PortalContent: FC<TPortalContentProps> = ({
  actions = null,
  className,
  content = null,
  "data-testid": dataTestId,
  keepOpenOnClickOutside,
  noTopCloseButton = false,
  onClose,
  ref,
  unwrapContent,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const stopPropagation = useStopPropagation();

  const onClickOutside = useCallback(() => {
    if (!keepOpenOnClickOutside) {
      onClose?.();
    }
  }, [onClose, keepOpenOnClickOutside]);

  const stopPropagationAndClose = useStopPropagation(onClickOutside);

  const wrapperOnClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === wrapperRef.current) {
        stopPropagationAndClose(e);
      }
    },
    [stopPropagationAndClose],
  );

  const wrapperOnMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === wrapperRef.current) {
        stopPropagation(e);
      }
    },
    [stopPropagation],
  );

  useOnEscKeyDownEvent(onClose);

  const topCloseButton = noTopCloseButton
    ? null
    : (
        <IconButton onClick={onClose} size="medium" tooltip={t("buttons.close")}>
          <CloseIcon />
        </IconButton>
      );

  return (
    <div
      className="fixed inset-0 z-998 flex items-center justify-center bg-black/35 dark:bg-black/55"
      onClick={wrapperOnClick}
      onMouseDown={wrapperOnMouseDown}
      ref={wrapperRef}
    >
      <div
        className={cn(
          "grid max-h-[95vh] max-w-[90vw] grid-rows-[auto_1fr_auto] gap-2 rounded-lg border border-border bg-popover p-5 shadow-xl",
          className,
        )}
        data-testid={dataTestId}
        ref={ref}
      >
        {topCloseButton && <div className="flex items-center justify-end">{topCloseButton}</div>}
        {unwrapContent ? content : <div className="overflow-auto">{content}</div>}
        {actions && <div className="flex items-center justify-end gap-3">{actions}</div>}
      </div>
    </div>
  );
};

const Modal: FC<Omit<IModalProps, "isOpen"> & TWithTestId> = ({ parentElement, ...restProps }) => {
  const portalTarget = parentElement ?? document.body;
  return createPortal(<PortalContent {...restProps} />, portalTarget);
};

const ModalWrapper: FC<IModalProps & TWithTestId> = ({ isOpen, ...restProps }) => {
  const localPortal = useLocalOverlay();
  const parentElement = useMemo(() => {
    const chosenElement = restProps.parentElement ?? localPortal;
    if (chosenElement instanceof HTMLElement) return chosenElement;
    return undefined;
  }, [restProps.parentElement, localPortal]);

  return isOpen ? <Modal {...restProps} parentElement={parentElement} /> : null;
};

export { ModalWrapper as Modal, Modal as UnWrappedModal };
